import {HttpClient, withPublicHttpClient} from '@raison/network-facade';
import classNames from 'classnames';
import {isString} from 'effect/Predicate';
import React, {Component, RefObject} from 'react';
import {connect} from 'react-redux';
import * as config from '~/config';
import {logout} from '~/entities/user';
import {ApiError} from '~/redux/types/common/ServerErrorType';
import {smsDeliveryStatus} from '~/shared/api/legacy_services/delivery';
import TenantId from '~/shared/legacy_models/TenantId';
import SubmissionError from '~/shared/legacy_presentational-components/SubmissionError/SubmissionError';
import {invariant} from '~/shared/utils/types';
import styles from './Countdown.module.scss';

const {SERVICE_UNAVAILABLE_MESSAGE} = config;

type Props = {
    text?: string;
    resendHandler: () => Promise<void>;
    seconds: number;
    useForSignup?: boolean;
    resendStyles?: string;
    accountClosure?: boolean;
    http: HttpClient;
    logout: () => void;
};

type State = {
    error: ApiError | null;
    loading: string | null;
    time: number;
    countdown: string;
    seconds: number;
};

class Countdown extends Component<Props, State> {
    timer: number;
    myRef: RefObject<HTMLDivElement>;

    constructor(props: Props) {
        super(props);
        this.state = {
            error: null,
            loading: null,
            time: 0,
            countdown: '',
            seconds: props.seconds ?? 60,
        };
        this.timer = 0;
        this.startTimer = this.startTimer.bind(this);
        this.countDown = this.countDown.bind(this);
        this.clickHandler = this.clickHandler.bind(this);
        this.onCountDownFinished = this.onCountDownFinished.bind(this);
        this.myRef = React.createRef();
    }

    setError(code: number, message: string) {
        this.setState({error: {code, message}});
    }

    startTimer() {
        if (this.timer === 0) {
            this.countDown();
            this.timer = window.setInterval(this.countDown, 1000);
        }
    }

    async clickHandler() {
        this.setState({loading: 'Sending...'});
        try {
            await this.props.resendHandler();
        } finally {
            this.setState({loading: null});
        }
        if (this.myRef.current) {
            this.setState({
                time: 0,
                countdown: '',
                seconds: this.props.seconds,
            });
        }
        this.timer = 0;
        this.startTimer();
    }

    componentDidMount() {
        if (this.myRef.current) {
            this.setState({
                time: this.props.seconds,
            });
        }
        this.startTimer();
    }

    componentDidUpdate(prevProps: Props) {
        if (this.props.seconds !== prevProps.seconds)
            this.setState({
                time: 0,
                countdown: '',
                seconds: this.props.seconds,
            });
    }

    countDown() {
        if (this.state.seconds === 0) {
            this.onCountDownFinished();
            return;
        }

        const time = this.state.seconds - 1;
        if (this.myRef.current) {
            const fullSeconds = this.state.seconds % 3600;
            let minutes = Math.floor(fullSeconds / 60).toString();
            let seconds = (fullSeconds % 60).toString();

            minutes = String(minutes).padStart(2, '0');
            seconds = String(seconds).padStart(2, '0');

            this.setState(state => {
                return {
                    ...state,
                    time: time,
                    seconds: time,
                    countdown: (minutes ? `${minutes}:` : '') + seconds,
                };
            });
        }

        if (time === 0) {
            this.onCountDownFinished();
        }
    }

    async onCountDownFinished() {
        const tenantId = new TenantId();
        clearInterval(this.timer);
        this.setState({time: 0, countdown: '', seconds: this.props.seconds, loading: 'Checking...'});

        if (!this.props.accountClosure) {
            const tenantIdString = tenantId.get();
            invariant(isString(tenantIdString), 'tenantIdString is undefined');
            const {status, json} = await smsDeliveryStatus(this.props.http, tenantIdString).finally(() => {
                this.setState({loading: null});
            });
            if (status === 401) {
                this.props.logout();
                return;
            }
            if (status === 400 || status === 404) {
                return this.setError(json.code, SERVICE_UNAVAILABLE_MESSAGE);
            }
            const {code, message} = json;
            if ('number' !== typeof code || !Number.isFinite(code) || Number.isNaN(code)) {
                return this.setError(code, SERVICE_UNAVAILABLE_MESSAGE);
            }
            if (code < 0 || code >= 100) {
                return this.setError(code, message ?? 'Delivery failed, please contact us.');
            }
        }
        this.setState({loading: null});
    }

    renderResendButton() {
        const {useForSignup} = this.props;

        if (useForSignup) {
            return (
                <span className={styles.resendLink} onClick={this.clickHandler}>
                    Resend code
                </span>
            );
        } else {
            return (
                <span className={this.props.resendStyles || styles.psLink} onClick={this.clickHandler}>
                    resend
                </span>
            );
        }
    }

    render() {
        const {text: textProp, useForSignup} = this.props;
        const {countdown, loading, error} = this.state;

        const text = textProp ?? 'New code will be available in ';
        const showCounter = Boolean(countdown);

        return (
            <div ref={this.myRef} className={this.props.accountClosure ? styles.accountClosure : ''}>
                {!useForSignup && <p className={styles.baseText}>{"Didn't receive anything from us?"}</p>}
                <div
                    className={classNames(styles.resendContainer, useForSignup && styles.resendContainer_useForSignup)}
                >
                    {loading && <p className={styles.baseText}>{loading}</p>}
                    {!loading && showCounter && (
                        <div className={classNames(styles.baseText, useForSignup && styles.baseText_useForSignup)}>
                            <span className={styles.paddingRight}>{text}</span>
                            <div className={classNames(styles.boldText, useForSignup && styles.boldText_useForSignup)}>
                                {countdown}
                            </div>
                        </div>
                    )}
                    {!loading && !showCounter && !error && this.renderResendButton()}
                    {!loading && !showCounter && error && (
                        <SubmissionError error={error} errorStyleClass={styles.error} />
                    )}
                </div>
            </div>
        );
    }
}

export default connect(null, {logout})(withPublicHttpClient(Countdown));
